import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  ListItem,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  UnorderedList
} from '@chakra-ui/react'

import { DetailSheet } from '../../../components/DetailSheet'
import { EMPTY_CELL_VALUE, EmptyCell } from '../../../components/EmptyCell'
import { organizationalLevelLabel } from '../../../components/EvaluatedEmployeeFields'
import { MASKS, PIX_KEY_MASKS } from '../../../components/MaskedInput'
import { SectionTitle } from '../../../components/SectionCard'
import ShortText from '../../../components/ShortText'
import { StatusBadge } from '../../../components/StatusBadge'
import { formatApiDate, formatDateTime, formatPhone, formatZipCode, PixKeyType } from '../../../helpers'
import { TTransportBenefit } from '../../../services/Admission/types'
import { IEmployeeDetailRaw } from '../../../services/Employee/types'
import {
  BENEFIT_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  PIX_KEY_TYPE_OPTIONS,
  RACE_OPTIONS,
  TRANSPORT_MODES,
  TRANSPORT_ROUTES
} from '../Admission/admissionForm'
import { EMPLOYEE_STATUS, HISTORY_DATE_LABELS, HISTORY_EVENT_LABELS } from './employeeConstants'

const labelOf = <T extends string>(options: { value: T; label: string }[], value: T | null | undefined) =>
  options.find((option) => option.value === value)?.label ?? null

// The API keeps CPF and phone keys as digits; the admission field's mask formats them back.
const pixKeyLabel = (type: PixKeyType, key: string | null) =>
  `${labelOf(PIX_KEY_TYPE_OPTIONS, type)}: ${MASKS[PIX_KEY_MASKS[type]].format(key ?? '')}`

export function PersonalSheet({ detail }: { detail: IEmployeeDetailRaw }) {
  const address = [
    [detail.street, detail.number].filter(Boolean).join(', '),
    detail.complement,
    detail.neighborhood,
    [detail.city, detail.state].filter(Boolean).join(' - '),
    detail.zip_code ? `CEP ${formatZipCode(detail.zip_code)}` : null
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <DetailSheet
      layout="grid"
      columns={{ base: 1, md: 2 }}
      fields={[
        { label: 'Nome completo', value: detail.name },
        { label: 'Como gostaria de ser chamado(a)', value: detail.nickname },
        { label: 'E-mail', value: detail.email },
        { label: 'Telefone', value: formatPhone(detail.phone) },
        // No colSpan: spanning two columns would keep two columns on a phone too.
        { label: 'Endereço', value: address },
        {
          label: 'Conta Inter',
          value:
            detail.bank_agency || detail.bank_account
              ? `Banco ${detail.bank_code} · Agência ${detail.bank_agency ?? EMPTY_CELL_VALUE} · Conta ${detail.bank_account ?? EMPTY_CELL_VALUE}`
              : null
        },
        {
          label: 'Chave Pix',
          value: detail.pix_key_type ? pixKeyLabel(detail.pix_key_type, detail.pix_key) : null,
          emptyValue: 'Não informada'
        },
        { label: 'Estado civil', value: labelOf(MARITAL_STATUS_OPTIONS, detail.marital_status) },
        { label: 'Cor/Raça', value: labelOf(RACE_OPTIONS, detail.race) },
        {
          label: 'Filhos',
          value: detail.has_children === null ? null : detail.has_children ? `Sim (${detail.children_count})` : 'Não'
        }
      ]}
    />
  )
}

export function ProfessionalSheet({ detail }: { detail: IEmployeeDetailRaw }) {
  return (
    <DetailSheet
      layout="grid"
      columns={{ base: 1, md: 2 }}
      fields={[
        { label: 'Status', value: <StatusBadge status={detail.status} statuses={EMPLOYEE_STATUS} /> },
        { label: 'Nome de usuário', value: detail.username },
        { label: 'Cargo', value: detail.position },
        { label: 'Setor', value: detail.sector },
        {
          label: 'Nível organizacional',
          value: organizationalLevelLabel(detail.organizational_level)
        },
        detail.job_requisition && { label: 'Vaga', value: `RP nº ${detail.job_requisition.number}` },
        detail.submitted_at && { label: 'Enviado em', value: formatDateTime(detail.submitted_at) },
        detail.status !== 'pending' && { label: 'Data de Admissão', value: formatApiDate(detail.admission_date) },
        detail.status === 'inactive' && { label: 'Data de Desligamento', value: formatApiDate(detail.termination_date) }
      ]}
    />
  )
}

export function ContactsSheet({ detail }: { detail: IEmployeeDetailRaw }) {
  if (detail.emergency_contacts.length === 0) return <Text variant="caption">Nenhum contato de emergência.</Text>

  return (
    <Stack spacing={6}>
      {detail.emergency_contacts.map((contact, index) => (
        <Box key={index}>
          <SectionTitle as="h3">{index === 0 ? '1º contato' : '2º contato'}</SectionTitle>
          <DetailSheet
            layout="grid"
            columns={{ base: 1, md: 3 }}
            fields={[
              { label: 'Nome', value: contact.name },
              { label: 'Telefone', value: formatPhone(contact.phone) },
              { label: 'Grau de parentesco', value: contact.relationship }
            ]}
          />
        </Box>
      ))}
    </Stack>
  )
}

// The term and the browser are long; the full text shows on hover.
const ACCEPTANCE_TEXT_CHARS = 100

const benefitLabels = (benefits: TTransportBenefit[] | null | undefined) =>
  (benefits ?? []).map((benefit) => labelOf(BENEFIT_OPTIONS, benefit) ?? benefit).join(', ')

export function BenefitsSheet({ detail }: { detail: IEmployeeDetailRaw }) {
  const acceptance = detail.benefit_acceptance
  const routes = detail.transport_routes

  return (
    <Stack spacing={6}>
      <DetailSheet fields={[{ label: 'Benefícios de transporte', value: benefitLabels(detail.transport_benefits) }]} />

      {acceptance && !detail.benefit_acceptance_matches && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Os benefícios atuais diferem do que a pessoa aceitou ({benefitLabels(acceptance.options)}).
          </AlertDescription>
        </Alert>
      )}

      <Box>
        <SectionTitle as="h3">Aceite eletrônico</SectionTitle>
        {acceptance ? (
          <DetailSheet
            fields={[
              { label: 'Data e hora', value: formatDateTime(acceptance.accepted_at) },
              { label: 'Opções aceitas', value: benefitLabels(acceptance.options) },
              { label: 'IP', value: acceptance.ip },
              {
                label: 'Navegador',
                value: <ShortText text={acceptance.user_agent} beforeChars={ACCEPTANCE_TEXT_CHARS} afterChars={0} />
              },
              {
                label: 'Termo',
                value: <ShortText text={acceptance.term} beforeChars={ACCEPTANCE_TEXT_CHARS} afterChars={0} />
              }
            ]}
          />
        ) : (
          <Text variant="caption">
            Sem aceite: o colaborador foi cadastrado direto pelo RH ou não escolheu benefício.
          </Text>
        )}
      </Box>

      {routes && (
        <Box>
          <SectionTitle as="h3">Vale-Transporte</SectionTitle>
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  <Th>Trajeto</Th>
                  {TRANSPORT_MODES.map((mode) => (
                    <Th key={mode.value}>{mode.label}</Th>
                  ))}
                  <Th>Integração</Th>
                </Tr>
              </Thead>
              <Tbody>
                {TRANSPORT_ROUTES.map((route) => {
                  const saved = routes[route.value]
                  return (
                    <Tr key={route.value}>
                      <Td>{route.title}</Td>
                      {TRANSPORT_MODES.map((mode) => (
                        <Td key={mode.value}>
                          <EmptyCell value={saved?.[mode.value]} />
                        </Td>
                      ))}
                      <Td>
                        <EmptyCell value={saved?.integration == null ? null : saved.integration ? 'Sim' : 'Não'} />
                      </Td>
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Stack>
  )
}

export function HistorySheet({ detail }: { detail: IEmployeeDetailRaw }) {
  if (detail.history.length === 0) return <Text variant="caption">Nenhum registro no histórico.</Text>

  return (
    <UnorderedList spacing={3} pl={1}>
      {detail.history.map((event, index) => (
        <ListItem key={index}>
          <Text>
            <strong>{HISTORY_EVENT_LABELS[event.event]}</strong>
            {event.by ? ` por ${event.by}` : ''} em {formatDateTime(event.at)}
          </Text>
          {event.date && HISTORY_DATE_LABELS[event.event] && (
            <Text variant="caption">
              {HISTORY_DATE_LABELS[event.event]}: {formatApiDate(event.date)}
            </Text>
          )}
          {event.note && <Text variant="caption">{event.note}</Text>}
        </ListItem>
      ))}
    </UnorderedList>
  )
}
